import React, { useState, Children, useEffect, useLayoutEffect } from 'react'
import { TabProps, Tab } from './Tab'
import { TabHeader } from './components'
import { useWindowSize } from '../../hooks/useWindowSize'
import { SliderContent } from '../Animations/Slider/SliderContent'
import { navigateToSlideIndex } from '../Animations/Slider/slider.helpers'
import { twMerge } from 'tailwind-merge'
import { useSearchParams } from 'react-router-dom'
import { UseState } from '@unimpaired/utils'

type BaseProps = {
  className?: string
  tabHeaderClasses?: string
  children: (React.FunctionComponentElement<TabProps> | React.ReactNode)[] // Children Should have step props
  fullPage?: boolean
  routing?: boolean
  disableRouting?: boolean
  tabTitleClasses?: string
}
type UncontrolledProps = BaseProps & {
  defaultTab?: number
  tab?: never
  setTab?: never
}
type ControlledProps = BaseProps & {
  defaultTab?: never
  tab: number
  setTab: (tab: number) => void
}

export const Tabs = (props: UncontrolledProps | ControlledProps) => {
  const internalState = useState((props.defaultTab || 1) - 1)
  const controlledState =
    props.tab && props.setTab
      ? ([props.tab, props.setTab] as UseState<number>)
      : undefined

  const [currentTabIndex, setCurrentTabIndex] = controlledState || internalState

  const [queryParams, setQueryParams] = useSearchParams()

  const [stepPropsList, setTabPropsList] = useState<TabProps[]>([])

  useLayoutEffect(() => {
    const tabParam = queryParams.get('tab')
    const tabIndex = stepPropsList
      .map((prop, index) => ({ index, ...prop }))
      .find((props) => props.title === tabParam)?.index
    if (!tabIndex) return
    if (currentTabIndex === tabIndex) return
    setCurrentTabIndex(tabIndex)
  }, [stepPropsList, currentTabIndex])

  useEffect(() => {
    setTabPropsList(
      // @ts-ignore
      Children.toArray(props.children).map((child) => child.props),
    )
  }, [props.children])

  useEffect(() => {
    setTabPropsList((prevProps) =>
      prevProps.map((prop, i) => {
        if (i < currentTabIndex) return { ...prop, completed: true }
        return prop
      }),
    )
  }, [currentTabIndex])

  return (
    <section
      className={twMerge(
        `w-full box-border flex flex-col justify-between items-stretch grow overflow-clip`,
        props.className,
      )}>
      <TabHeader
        routing={props.routing}
        tabTitleClasses={props.tabTitleClasses}
        className={props.tabHeaderClasses}
        currentStep={currentTabIndex}
        stepPropsList={stepPropsList}
        onClickTitle={(tabIndex) => {
          setCurrentTabIndex(
            navigateToSlideIndex(tabIndex, currentTabIndex, stepPropsList),
          )
          if (!props.disableRouting) {
            setQueryParams((prev) => ({
              ...prev,
              tab: stepPropsList?.[tabIndex].title,
            }))
          }

          if (stepPropsList[tabIndex].onClick) stepPropsList[tabIndex].onClick()
        }}
      />

      <SliderContent
        selectedSlide={currentTabIndex}
        className={
          stepPropsList[currentTabIndex]?.className
            ? stepPropsList[currentTabIndex].className
            : undefined
        }>
        {props.children}
      </SliderContent>
    </section>
  )
}

Tabs.Tab = Tab

const isControlled = (props: BaseProps): props is ControlledProps => {
  return 'tab' in props && 'setTab' in props
}
const isUncontrolled = (props: BaseProps): props is UncontrolledProps => {
  return 'tab' in props && 'setTab' in props
}
